import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Send, Users, Clock, CheckCircle, XCircle, Image, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkEmail = () => {
  const [newsletter, setNewsletter] = useState({
    subject: '',
    body: ''
  });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subscribersRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/newsletters`),
        axios.get(`${API}/admin/email-logs`)
      ]);
      const activeSubscribers = subscribersRes.data.filter(s => s.active !== false);
      setSubscriberCount(activeSubscribers.length);
      setEmailLogs(logsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, WEBP)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData);
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      
      // Insert image tag at cursor or end
      const imgTag = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto;" />`;
      setNewsletter(prev => ({
        ...prev,
        body: prev.body + '\n' + imgTag
      }));
      toast.success('Image uploaded! Tag added to email body.');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const insertImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      const imgTag = `<img src="${url}" alt="Image" style="max-width: 100%; height: auto;" />`;
      setNewsletter(prev => ({
        ...prev,
        body: prev.body + '\n' + imgTag
      }));
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!newsletter.subject || !newsletter.body) {
      toast.error('Please fill in both subject and body');
      return;
    }

    if (subscriberCount === 0) {
      toast.error('No active subscribers to send to');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to send this newsletter to ${subscriberCount} subscribers?`);
    if (!confirmed) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/send-newsletter`, newsletter);
      toast.success(response.data.message);
      setNewsletter({ subject: '', body: '' });
      // Refresh logs after a delay
      setTimeout(fetchData, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  return (
    <div className="admin-section">
      <h2 className="section-subtitle" style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Send size={24} />
        Send Newsletter
      </h2>

      {/* Subscriber Count */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '15px', 
        padding: '20px', 
        background: 'var(--bg-primary)', 
        borderRadius: '8px',
        marginBottom: '30px'
      }}>
        <Users size={32} style={{ color: 'var(--accent-gold)' }} />
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Active Subscribers</p>
          <p style={{ color: 'var(--accent-silver)', fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>{subscriberCount}</p>
        </div>
      </div>

      <div className="admin-form">
        <form onSubmit={handleSend}>
          {/* Subject */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <Label>Email Subject *</Label>
            <Input
              value={newsletter.subject}
              onChange={(e) => setNewsletter({ ...newsletter, subject: e.target.value })}
              placeholder="New Book Release: A Journey Within"
              required
            />
          </div>

          {/* Image Upload Helper */}
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <Label>Add Images to Email</Label>
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <label style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '8px 16px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                fontSize: '0.9rem'
              }}>
                <Upload size={16} />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>
              <Button type="button" onClick={insertImageUrl} className="btn-secondary" style={{ padding: '8px 16px' }}>
                <Image size={16} style={{ marginRight: '8px' }} />
                Insert URL
              </Button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '8px' }}>
              Upload an image or paste a URL. Images will be displayed inline in the email.
            </p>
          </div>

          {/* Body */}
          <div className="form-group" style={{ marginBottom: '30px' }}>
            <Label>Email Body (HTML) *</Label>
            <Textarea
              value={newsletter.body}
              onChange={(e) => setNewsletter({ ...newsletter, body: e.target.value })}
              placeholder="<p>Dear Reader,</p><p>I'm excited to announce...</p>"
              rows={12}
              style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
              required
            />
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '8px' }}>
              HTML tags supported: &lt;p&gt;, &lt;strong&gt;, &lt;a href=""&gt;, &lt;img src=""&gt;, etc. Unsubscribe link added automatically.
            </p>
          </div>

          {/* Preview */}
          {newsletter.body && (
            <div style={{ marginBottom: '30px' }}>
              <Label>Preview</Label>
              <div style={{
                marginTop: '10px',
                padding: '20px',
                background: '#1a1a1a',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontFamily: 'Georgia, serif',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                <div style={{ textAlign: 'center', borderBottom: '1px solid #333', paddingBottom: '15px', marginBottom: '15px' }}>
                  <h1 style={{ color: '#c0c0c0', margin: 0 }}>Heeloly Upasani</h1>
                </div>
                <div dangerouslySetInnerHTML={{ __html: newsletter.body }} />
                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #333', fontSize: '12px', color: '#888', textAlign: 'center' }}>
                  <p>You received this email because you subscribed to our newsletter.</p>
                  <p><a href="#" style={{ color: '#d4af37' }}>Unsubscribe</a></p>
                </div>
              </div>
            </div>
          )}

          <Button type="submit" className="btn-primary" disabled={sending || subscriberCount === 0}>
            <Send size={18} style={{ marginRight: '8px' }} />
            {sending ? 'Sending...' : `Send to ${subscriberCount} Subscribers`}
          </Button>
        </form>
      </div>

      {/* Email Logs */}
      {emailLogs.length > 0 && (
        <div style={{ marginTop: '50px' }}>
          <h3 style={{ color: 'var(--accent-silver)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Clock size={20} />
            Recent Newsletters
          </h3>
          <div style={{ background: 'var(--bg-card)', borderRadius: '8px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-primary)' }}>
                  <th style={{ padding: '12px 15px', textAlign: 'left', color: 'var(--text-secondary)' }}>Subject</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', color: 'var(--text-secondary)' }}>Sent</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', color: 'var(--text-secondary)' }}>Failed</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', color: 'var(--text-secondary)' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.map((log, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px 15px', color: 'var(--text-primary)' }}>{log.subject}</td>
                    <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--accent-gold)' }}>
                        <CheckCircle size={16} /> {log.sent_count}
                      </span>
                    </td>
                    <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: log.failed_count > 0 ? 'var(--accent-red)' : 'var(--text-muted)' }}>
                        <XCircle size={16} /> {log.failed_count}
                      </span>
                    </td>
                    <td style={{ padding: '12px 15px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      {new Date(log.sent_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkEmail;
